import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Flex, Typography } from '@duxton-web/ui';
import { produce } from 'immer';

import ConstraintsEditor from '@/common/ConstraintsEditor';
import Service, { Facet } from '@/lib/App';
import { Constraint } from '@/lib/PlannerDTOs';
import { getVariableFacets } from '@/utils/variable';

import './index.css';

import { ConstraintOperator } from '@/common/ConstraintsEditor/Constraint/facetMap';
import EnvironmentToggle from '@/components/environment-toggle';
import { useVariableContext } from '@/features/variable/contexts/variable-context';
import { useGetVar } from '@/hooks/usePlannerQueries';

enum ResolveInfoType {
  RESOLVE_BY = 'Resolved By',
  RESOLVE_WITH = 'Resolved With',
  VARIANT_ID = 'Variant ID',
}

type ResolveInfoData = {
  label: string;
  value: string;
};

const Result = ({ response }) => {
  const { variable } = useVariableContext();

  if (!response) return <></>;

  let responseMessage: { [key: string]: React.ReactNode } = {};
  const { resolvedBy, resolvedWith, variantID, string, value } = response;

  const resolveInfo: ResolveInfoData[] = [
    {
      label: ResolveInfoType.RESOLVE_BY,
      value: resolvedBy,
    },
    {
      label: ResolveInfoType.RESOLVE_WITH,
      value: resolvedWith,
    },
    {
      label: ResolveInfoType.VARIANT_ID,
      value: variantID,
    },
  ];

  const valueString = `${value} ${string ? `(${string})` : ''}`;

  responseMessage = {
    'Current Value:': <Typography.Text>{valueString}</Typography.Text>,
  };

  if (resolvedBy) {
    responseMessage['Resolve Info:'] = (
      <Typography.Paragraph
        style={{
          marginBottom: 0,
        }}
      >
        <ul
          style={{
            marginBlock: 0,
          }}
        >
          {resolveInfo.map((info: ResolveInfoData) => {
            if (info.label === ResolveInfoType.VARIANT_ID) {
              return (
                <li key={info.label}>
                  <Flex gap={4}>
                    <Typography.Text>{`${info.label}: `}</Typography.Text>
                    <Typography.Link
                      href={`/t/variables/${variable?.name}/rollout?id=${info.value}`}
                      target="_blank"
                    >
                      {info.value}
                    </Typography.Link>
                  </Flex>
                </li>
              );
            } else
              return (
                <li key={info.label}>
                  <Flex gap={4}>
                    <Typography.Text>{`${info.label}: `}</Typography.Text>
                    <Typography.Text>{info.value}</Typography.Text>
                  </Flex>
                </li>
              );
          })}
        </ul>
      </Typography.Paragraph>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Object.keys(responseMessage).map((key: string) =>
        responseMessage[key] ? (
          <Alert
            key={key}
            message={key}
            description={responseMessage[key]}
            type="info"
          />
        ) : null,
      )}
    </div>
  );
};

const TipsAlert = () => (
  <Alert
    message="Tips:"
    description={
      <Typography.Paragraph
        style={{
          marginBottom: 0,
        }}
      >
        <ul>
          <li>
            Please input values for all constraints used during the
            getVariable() call. If values are missing in this call, you may not
            get your expected result
          </li>
          <li>Time facet is always sent as current timestamp.</li>
          <li>
            If group is used in experiment/rollout, please click Get Variable
            button several times.
          </li>
        </ul>
      </Typography.Paragraph>
    }
    type="info"
    showIcon
  />
);

const GetVar = ({
  testConstraints = [],
}: {
  testConstraints?: Constraint[];
}) => {
  const { variable } = useVariableContext();

  const { name, backend } = variable || {
    name: '',
    backend: '',
  };

  const [constraints, setConstraints] = useState<Constraint[]>(testConstraints);
  const tempConstraintsUpdateRef = useRef<Constraint[]>([]);
  const hasTestConstraints = useRef(testConstraints.length > 0);

  const { data, isLoading, error, refetch } = useGetVar(
    name,
    backend,
    constraints,
    {
      enabled: false,
    },
  );

  const isDisabled: boolean =
    !constraints?.length ||
    !constraints.some(({ value }: Constraint): boolean => !!value);

  useEffect(() => {
    // Don't override constraints if testConstraints were provided
    if (hasTestConstraints.current) return;

    let variableFacets = variable?.facets;
    const facetConstraints: Constraint[] = [];

    if (!variableFacets?.length) return;

    variableFacets = variable.facets!.filter((name: string) => name !== 'ts');
    const facets = Service.cache!.getAllFacets().filter(({ name }: Facet) =>
      variableFacets.includes(name),
    );

    facets.forEach((facet) => {
      facetConstraints.push({
        target: facet.name,
        op: ConstraintOperator.EQUALS.value,
      });
    });

    setConstraints(facetConstraints);
    tempConstraintsUpdateRef.current = JSON.parse(
      JSON.stringify(facetConstraints),
    );
  }, [variable]);

  const onConstraintsUpdate = (constraints: Constraint[]): void => {
    const nextConstraints = produce(constraints, (draft) => {
      draft.forEach(
        (constraint) => (constraint.op = ConstraintOperator.EQUALS.value),
      );
    });
    setConstraints(nextConstraints);
  };

  return (
    <>
      <div
        id="variable-get-value"
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        <TipsAlert />
        <EnvironmentToggle />
        <ConstraintsEditor
          skipFirst
          onlyEqualOperator
          constraints={constraints || []}
          onChange={onConstraintsUpdate}
          allowedFacets={getVariableFacets(variable)}
          rolloutVariable={variable}
          disableTarget
          disableAddConstraint
          showFacetStatus={false}
          disableDeleteConstraint
          testingPurpose={true}
        />
        <Button
          type="primary"
          shape="round"
          size="small"
          style={{
            width: 'fit-content',
            alignSelf: 'flex-end',
          }}
          disabled={isLoading || isDisabled}
          loading={isLoading}
          onClick={() => refetch()}
        >
          Get Variable
        </Button>
        <Result response={data?.data?.[0]?.[name]} />
        {error?.message && (
          <div>
            <h2>Error:</h2>
            <div>{error.message}</div>
          </div>
        )}
      </div>
    </>
  );
};

export default GetVar;
